"use strict";
/**
 * On-device connectivity diagnosis.
 *
 * Why this exists: browsers collapse every network failure into one opaque
 * string — "Failed to fetch" in Chrome, "Load failed" in Safari — for DNS
 * failure, TLS failure, connection reset, blackholed packets and captive
 * portals alike. The message tells you nothing about the mechanism.
 *
 * Time-to-failure does. A connection actively refused comes back in
 * milliseconds; a DNS failure takes a second or two; silently dropped packets
 * take tens of seconds. That single number separates "something is rejecting
 * us in-path" from "packets are going into a void", which is the distinction
 * worth having when the operator is standing in a stadium with no laptop.
 *
 * Probes /health, which requires no session, so the probe itself is never
 * confounded by an expired or rejected session — a failure here is the network.
 *
 * NOT on the order hot path. This runs only when the operator taps the button.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.probeConnection = probeConnection;
exports.formatDiagnosticReport = formatDiagnosticReport;
const request_1 = require("../api/request");
const network_failure_log_1 = require("../api/network-failure-log");
/**
 * Bounded so a blackholed network eventually reports rather than spinning
 * forever. Comfortably above the ~8s blackhole threshold below, so a silent
 * drop is still distinguishable from a slow-but-alive network.
 */
const PROBE_TIMEOUT_MS = 15000;
/** Below this, the connection was actively refused rather than lost. */
const REFUSED_MS = 500;
/** Above this, nothing replied at all — packets are being dropped. */
const BLACKHOLE_MS = 8000;
const EXPLANATIONS = {
    reachable: "Server reached successfully. The network is not blocking the app — if requests are still failing, the problem is elsewhere.",
    "server-error": "Reached the server, but it answered with an error. The network is fine; the app or its dependencies are not.",
    refused: "Connection refused immediately. Something in the path actively rejected it — a firewall sending a reset, or DNS reporting no such host. Typical of a network deliberately blocking this domain.",
    "dns-or-tls": "Failed after a short delay. Most likely DNS resolution failing, or a TLS handshake being cut off.",
    blackholed: "No reply at all before timing out. Packets are being silently dropped, which is what a filtering firewall or an unauthenticated captive portal usually looks like.",
};
/** Classify a failed probe by how long it took to fail. */
function classifyFailure(durationMs) {
    if (durationMs < REFUSED_MS)
        return "refused";
    if (durationMs < BLACKHOLE_MS)
        return "dns-or-tls";
    return "blackholed";
}
/**
 * Probe /health and classify the outcome.
 *
 * Never throws — the whole point is to report a failure, so a rejection here
 * is data, not an error.
 */
async function probeConnection() {
    const path = "/health";
    const url = `${(0, request_1.getApiBaseUrl)()}${path}`;
    const started = performance.now();
    try {
        // A timeout is safe here in a way it is not on the order path: /health
        // is idempotent and carries no transaction, so abandoning it cannot
        // leave anything in an unknown state.
        const response = await fetch(url, {
            method: "GET",
            signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
        });
        const durationMs = Math.round(performance.now() - started);
        const verdict = response.ok ? "reachable" : "server-error";
        return {
            verdict,
            durationMs,
            status: response.status,
            explanation: EXPLANATIONS[verdict],
        };
    }
    catch (err) {
        const durationMs = Math.round(performance.now() - started);
        const detail = err instanceof Error ? err.message : String(err);
        const verdict = classifyFailure(durationMs);
        (0, network_failure_log_1.recordNetworkFailure)({ at: Date.now(), path, detail, durationMs });
        return { verdict, durationMs, detail, explanation: EXPLANATIONS[verdict] };
    }
}
/**
 * Build a plain-text report of a probe plus recent failures, suitable for the
 * copy button — so the operator can paste it into a message rather than
 * describing what they saw from memory.
 */
function formatDiagnosticReport(result) {
    const lines = [
        "--- connection check ---",
        `when:     ${new Date().toISOString()}`,
        `origin:   ${(0, request_1.getApiBaseUrl)()}`,
        `verdict:  ${result.verdict}`,
        `duration: ${result.durationMs}ms`,
    ];
    if (result.status !== undefined)
        lines.push(`status:   ${result.status}`);
    if (result.detail)
        lines.push(`detail:   ${result.detail}`);
    // navigator.onLine only reports whether SOME interface is up, so it says
    // nothing about reachability — but a false here is conclusive, and it is
    // free to include.
    if (typeof navigator !== "undefined") {
        lines.push(`onLine:   ${navigator.onLine}`);
    }
    const failures = (0, network_failure_log_1.getRecentNetworkFailures)();
    if (failures.length > 0) {
        lines.push("", "recent failures (newest first):");
        for (const f of failures) {
            const duration = f.durationMs === undefined ? "—" : `${f.durationMs}ms`;
            lines.push(`  ${new Date(f.at).toISOString()}  ${f.path}  ${duration}  ${f.detail}`);
        }
    }
    return lines.join("\n");
}
